import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx';

import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';

import SendIcon from '../../svgs/SendIcon';
import CloseIcon from '../../svgs/CloseIcon';
import CircleCheckIcon from '../../svgs/CircleCheckIcon';
import CircleExclammationIcon from '../../svgs/CircleExclammationIcon';

type ToastState = 'success' | 'warning' | 'publishing' | 'loading' | 'error';

const getBgColor = (state: ToastState) => {
  if (state === 'success') return '#17C964';
  if (state === 'publishing') return '#F2D093';
  return '#F178B6';
};

const getStatusIcon = (state: ToastState) => {
  if (state === 'success') return <CircleCheckIcon />;
  if (state === 'publishing') return <SendIcon />;
  return <CircleExclammationIcon />;
};

const ToastNotification = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const {
    state = 'success',
    message,
    delay = 5000,
    position = 'bottom',
    persistent = false,
  } = videoCreator.toastState!;

  const [slidingOut, setSlidingOut] = useState(false);
  const [hasFocus, setHasFocus] = useState(document.hasFocus());
  const [isVisible, setIsVisible] = useState(false);

  const startTimeRef = useRef(Date.now());
  const elapsedTimeRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout>();
  const hasInitializedRef = useRef(false);

  const handleFocusChange = React.useCallback(() => {
    const newHasFocus = document.hasFocus();
    const currentTime = Date.now();

    if (!newHasFocus) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      elapsedTimeRef.current += currentTime - startTimeRef.current;
    } else {
      startTimeRef.current = currentTime;
    }

    setHasFocus(newHasFocus);
  }, []);

  const handleClose = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setSlidingOut(true);
    },
    [],
  );

  // Initial visibility setup
  useEffect(() => {
    if (!hasInitializedRef.current && hasFocus) {
      hasInitializedRef.current = true;
      setIsVisible(true);
    }
  }, [hasFocus]);

  // Focus event listeners
  useEffect(() => {
    window.addEventListener('focus', handleFocusChange);
    window.addEventListener('blur', handleFocusChange);

    return () => {
      window.removeEventListener('focus', handleFocusChange);
      window.removeEventListener('blur', handleFocusChange);
    };
  }, [handleFocusChange]);

  // Timer management
  useEffect(() => {
    if (persistent || delay < 0 || slidingOut) return;

    const remainingTime = Math.max(0, delay - elapsedTimeRef.current);

    if (hasFocus && remainingTime > 0) {
      startTimeRef.current = Date.now();
      timerRef.current = setTimeout(() => setSlidingOut(true), remainingTime);

      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }
  }, [hasFocus, persistent, delay, slidingOut]);

  // Sliding out animation and cleanup
  useEffect(() => {
    if (!slidingOut) return;

    const timer = setTimeout(() => {
      runInAction(() => {
        videoCreator.toastState = null;
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [slidingOut, videoCreator]);

  if (!isVisible) return null;

  return (
    <Main
      bgColor={getBgColor(state)}
      slidingOut={slidingOut}
      position={position}
    >
      {getStatusIcon(state)}
      {message}
      <Close onClick={handleClose}>
        <CloseIcon strokeColor="currentColor" />
      </Close>
    </Main>
  );
});

export default ToastNotification;

const bottomSlideUp = keyframes`
  from {
    transform: translateY(100%) translateX(-50%);
  }
  to {
    transform: translateY(0) translateX(-50%);
  }
`;

const bottomSlideDown = keyframes`
  from {
    transform: translateY(0) translateX(-50%);
  }
  to {
    transform: translateY(100%) translateX(-50%);
  }
`;

const topSlideUp = keyframes`
  from {
    transform: translateY(0%) translateX(-50%);
  }
  to {
    transform: translateY(-100%) translateX(-50%);
  }
`;

const topSlideDown = keyframes`
  from {
    transform: translateY(-100%) translateX(-50%);
  }
  to {
    transform: translateY(0) translateX(-50%);
  }
`;

const Main = styled.div<{
  bgColor: string;
  slidingOut: boolean;
  position: 'top' | 'bottom';
}>`
  text-align: center;
  padding: 0 15px;
  height: 60px;
  border-radius: 8px;
  gap: 10px;
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  ${(props) =>
    props.position === 'bottom' &&
    css`
      bottom: 20px;
      animation: ${props.slidingOut ? bottomSlideDown : bottomSlideUp} 0.5s
        ease-in-out;
    `}
  ${(props) =>
    props.position === 'top' &&
    css`
      top: 20px;
      animation: ${props.slidingOut ? topSlideUp : topSlideDown} 0.5s
        ease-in-out;
    `}
  background-color: ${(props) => props.bgColor};
  box-shadow: 8px 8px 16px 0px #00000066;
  color: #03041a;
  font-weight: 700;
  font-size: 14px;
  line-height: 16.94px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000001;
`;

const Close = styled.button`
  background-color: transparent;
  outline: none;
  border: none;
  cursor: pointer;
`;
